import { Dispatch, SetStateAction } from 'react'

/**
 * Use to guard against unprocessed values in switch-default, mostly for enums.
 * Produces errors at compile time and in runtime.
 */
export class UnprocessedValueError extends Error {
  constructor(typeName: string, unprocessedValue: never) {
    super(`Unprocessed value of ${typeName}: ${unprocessedValue}`)
  }
}

export const throwError =
  (message: string): never => {
    throw new Error(message)
  }

export const ensureString =
  (value: unknown): string => {
    // NOTE: Generic version with DUs and template literal types doesn't work
    if (typeof value === 'string') {
      return value
    }

    throw new Error(`Bad type - expected string, got ${(typeof value)}: ${JSON.stringify(value)}`)
  }

export const ensureNumber =
  (value: unknown): number => {
    if (typeof value === 'number') {
      if (isNaN(value)) {
        throw new Error('Bad number - NaN')
      }

      return value
    }

    throw new Error(`Bad type - expected number, got ${(typeof value)}: ${JSON.stringify(value)}`)
  }

export const tryParseNumber =
  (value: string | number | null | undefined): number | undefined => {
    switch (typeof value) {
      case 'number':
        return !isNaN(value) ? value : undefined

      case 'string':
        const valueParsed = parseInt(value)
        return !isNaN(valueParsed) ? valueParsed : undefined

      default:
        return undefined
    }
  }

export type SetStateFunc<S> = Dispatch<SetStateAction<S>>

/**
 * Constraints the type `T` to satisfy the `TConstraint`.
 */
export type Constrained<TConstraint, T extends TConstraint> =
  T extends NoInfer<TConstraint> ? T : never

export type Nullable<T> = T | null

export type Optional<T> = T | undefined

export type NullableMap<T> = {
  [K in keyof T]: Nullable<T[K]>
}

export type OptionalMap<T> = {
  [K in keyof T]: Optional<T[K]>
}

export type NonNullableMap<T> = {
  [K in keyof T]: NonNullable<T[K]>
}

export type SomeNonNullable<T, TKeys extends keyof T> =
  OmitStrict<T, TKeys> &
  NonNullableMap<Pick<T, TKeys>>

export type StringKeyOf<T> = string & keyof T

export type KeyForValueOf<T, TKey extends StringKeyOf<T>, TValue> =
  T[TKey] extends TValue ? TKey : never

/**
 * Omit that preserves discriminated unions.
 * See https://github.com/microsoft/TypeScript/issues/31501
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Omit2<T, K extends keyof any> =
  T extends unknown ? Omit<T, K>
                    : never

/**
 * Omit that constraints K to keys of T and preserves discriminated unions.
 */
export type OmitStrict<T, K extends keyof T> = Omit2<T, K>

export type Replace<
  TSource,
  TReplacement extends Partial<Record<keyof TSource, unknown>>
    // Disallow keys that don't exist in TSource
    & Record<Exclude<keyof TReplacement, keyof TSource>, never>> =
  OmitStrict<TSource, keyof TSource & keyof TReplacement>
  & TReplacement

/**
 * Creates a discriminated union from type T with either properties by TKey1 or by TKey2.
 */
export type SplitPick<
  T extends object,
  TKey1 extends Exclude<StringKeyOf<T>, TKey2>,
  TKey2 extends Exclude<StringKeyOf<T>, TKey1>> =
  (OmitStrict<T, TKey2> & Partial<Record<TKey2, undefined>>)
  | (OmitStrict<T, TKey1> & Partial<Record<TKey1, undefined>>)

/**
 * Makes the properties at the given keys optional.
 * @remarks Not using {@link Replace}, because it doesn't like the keys constraints.
 */
export type PartialMap<T, KPartial extends keyof T> =
  OmitStrict<T, KPartial> & Partial<Pick<T, KPartial>>

/**
 * Omits from `T` properties with keys matching `TShape`.
 */
export type OmitShape<T, TShape> = Omit<T, keyof TShape>

/**
 * Keys of required properties of `T`.
 */
export type RequiredKeys<T> = {
  [K in keyof T]-?: {} extends Pick<T, K> ? never
                                          : K
}[keyof T]

/**
 * Keys of optional properties of `T`.
 */
export type OptionalKeys<T> = {
  [K in keyof T]-?: {} extends Pick<T, K> ? K
                                          : never
}[keyof T]

/**
 * Picks required properties from `T`.
 */
export type PickRequired<T> = Pick<T, RequiredKeys<T>>

/**
 * Picks optional properties from `T`.
 */
export type PickOptional<T> = Pick<T, OptionalKeys<T>>

/**
 * Omits required properties from `T`.
 */
export type OmitRequired<T> = OmitStrict<T, RequiredKeys<T>>

/**
 * Omits optional properties from `T`.
 */
export type OmitOptional<T> = OmitStrict<T, OptionalKeys<T>>

/**
 * Like the built-in {@link Required}, but adds '| undefined' to properties that had a '?'.
 * @example {foo?: string, bar: number} => {foo: string | undefined, bar: number}
 */
export type Required2<T> =
  PickRequired<T> & OptionalMap<Required<PickOptional<T>>>

/**
 * Make all properties in `T` writable.
 */
export type Writable<T> = {
  -readonly [P in keyof T]: T[P]
}

export type StringMap<TValue = never> = {
  [key: string]: TValue
}

export type KeyValuePair<TKey, TValue> = {
  key: TKey
  value: TValue
}

/**
 * @example
 * EnumKey<typeof MyEnum>
 */
export type EnumKey<TEnum> =
  string & keyof TEnum

export type EnumConstraint<TEnum> = {
  [K in EnumKey<TEnum>]: TEnum[K]
}

/**
 * TODO: Is this useful outside of this file?
 * @example
 * EnumValue<typeof MyEnum>
 */
export type EnumValue<TEnum> =
  TEnum[EnumKey<TEnum>]

/**
 * Prevents inference of generic type parameters.
 */
export type NoInfer<T> = [T][T extends never ? never : 0]

/**
 * `never` if `T` is `never`, otherwise, `unknown`.
 */
export type NeverIfNever<T> = T extends never ? never : unknown

/**
 * Infers `true` if `T` is equivalent to `U`, otherwise `false`.
 *
 * Example:
 *
 * const test1: Equals&lt;string, string&gt; = true // ok
 *
 * const test2: Equals&lt;string, number&gt; = true // error at compile time
 */
export type Equals<T, U> =
  [T] extends [U]
  ? [U] extends [T]
    ? true
    : false
  : false

type UnionToIntersection<U> =
  (U extends unknown ? (arg: U) => 0 : never) extends (arg: infer I) => 0
  ? I
  : never

type LastInUnion<U> =
  UnionToIntersection<U extends unknown ? (x: U) => 0 : never> extends (x: infer L) => 0
  ? L
  : never

/**
 * Converts a union type into a tuple containing all the union elements:
 * UnionToTuple&lt;'foo' | 'bar'&gt; = ['foo', 'bar'].
 * HACK: Order in the resulting tuple might vary depending on build or TypeScript version.
 * Source - https://stackoverflow.com/a/69571688
 */
export type UnionToTuple<T, Last = LastInUnion<T>> =
  [T] extends [never]
  ? readonly []
  : readonly [...UnionToTuple<Exclude<T, Last>>, Last]

export type NonEmptyArray<T> =
  [T, ...T[]]

export type NonEmptyArrayReadOnly<T> =
  readonly [T, ...T[]]

/**
 * Identity function, ensures that the given `value` has type `T`.
 * TODO: Replace with satisfies keyword
 */
export const exact = <T = never>(value: NoInfer<T>): NoInfer<T> => value

/**
 * Identity function, ensures that the given `key` is indeed a key of type `T`.
 */
export const exactKeyOf =
  <T = never>() =>
    <K extends keyof T>(key: keyof Pick<NoInfer<T>, K>) => key

export const keysOf =
  <T extends object>(value: T) =>
    // It's fine to assert these types on our own objects
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    Object.keys(value) as Array<string & keyof T>

export const valuesOf =
  <T extends object>(value: T) =>
    // It's fine to assert these types on our own objects
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    Object.values(value) as Array<T[keyof T]>

export const entriesOf =
  <T extends Record<string & keyof T, T[keyof T]>>(value: T) =>
    // It's fine to assert these types on our own objects
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    Object.entries(value) as Array<[keyof T, T[keyof T]]>

export const isNotNull =
  <T>(value: T | null): value is T extends null ? never : T =>
    value !== null

export const isNotUndefined =
  <T>(value: T | undefined): value is T extends undefined ? never : T =>
    value !== undefined

export const isNotNullOrUndefined =
  <T>(value: T | null | undefined): value is NonNullable<T> =>
    value !== null && value !== undefined

export const isNumber =
  (value: unknown): value is number =>
    typeof value === 'number' && !isNaN(value)

export const valuesOfNumericEnum =
  <TEnum extends EnumConstraint<TEnum> & Record<string, unknown>>
  (enumType: TEnum): (EnumValue<TEnum> & number)[] =>
    Object.values(enumType)
          .filter(isNumber)
      // Preserve order
          .sort((value1, value2) => value1 - value2)

export const keysOfNumericEnum =
  <TEnum extends EnumConstraint<TEnum>>
  (enumType: TEnum): (EnumKey<TEnum>)[] =>
    valuesOfNumericEnum(enumType)
      .map(value => enumType[value])

export const hasEnumFlag =
  <T extends number>(targetValue: T, enumValue: T) =>
    (targetValue & enumValue) === enumValue

export const isEnumKey =
  <TEnum extends EnumConstraint<TEnum> & Record<string | number, unknown>>
  (value: string | null | undefined, enumType: TEnum): value is EnumKey<TEnum> => {
    if (value == null) {
      return false
    }

    const allKeys: string[] = keysOfNumericEnum(enumType) satisfies string[]

    // TODO: Add an option to ignore case and normalize for routing?
    return allKeys.includes(value)
  }

class TranspilerTestClass {
  testProp: unknown
}

export const checkTranspilerForClassProperties =
  () => new TranspilerTestClass().hasOwnProperty('testProp' satisfies keyof TranspilerTestClass)